'use client';

import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';

import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import {
  markReaderOpenedFromWorkPage,
  readReadingResumeBookmark,
} from '@/features/fanfic-page/utils/reading-resume.storage';

export interface UseFanficReaderNavigationReturn {
  readerUrl: string;
  handleReadClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleAuxClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface UseFanficReaderNavigationProps {
  fanficId: string;
  workPathPrefix?: '/fanfics' | '/originals';
  /** First published chapter id — used as SEO-friendly href for the Read button. */
  firstChapterId?: string | null;
}

/**
 * Hook for reader navigation (handling "Read" button clicks).
 * Bookmark is resolved on click (not synced via effect) to avoid cascading renders
 * and hydration mismatches. Href points at the first published chapter for SSR/SEO;
 * SPA click may still resume from a local bookmark.
 */
export const useFanficReaderNavigation = ({
  fanficId,
  workPathPrefix = '/fanfics',
  firstChapterId = null,
}: UseFanficReaderNavigationProps): UseFanficReaderNavigationReturn => {
  const router = useRouter();
  const { data: session } = useSession();

  const readerUrl = useMemo(() => {
    if (!firstChapterId) {
      return '';
    }

    return `${workPathPrefix}/${fanficId}/read/${firstChapterId}`;
  }, [fanficId, firstChapterId, workPathPrefix]);

  const handleReadClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.button === 2) {
        return;
      }

      if (event.ctrlKey || event.metaKey || event.button === 1) {
        return;
      }

      event.preventDefault();

      const bookmark = readReadingResumeBookmark(fanficId);
      const targetUrl = bookmark?.chapterId
        ? `${workPathPrefix}/${fanficId}/read/${bookmark.chapterId}`
        : readerUrl;

      if (targetUrl) {
        markReaderOpenedFromWorkPage(fanficId);
        router.push(targetUrl);
      }

      fetch(buildDirectBackendUrl('/api/reading-progress/read'), {
        method: 'POST',
        headers: createDirectBackendHeaders({
          accessToken: session?.accessToken,
          hasJsonContentType: true,
        }),
        body: JSON.stringify({
          postId: fanficId,
        }),
      }).catch((error) => {
        console.error('Failed to track read:', error);
      });
    },
    [session?.accessToken, fanficId, workPathPrefix, readerUrl, router],
  );

  const handleAuxClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1 || event.button === 2) {
      return;
    }
  }, []);

  return {
    readerUrl,
    handleReadClick,
    handleAuxClick,
  };
};
